import { addDays, endOfDay } from 'date-fns';
import { useUpdateTasks } from 'features/tasks';
import React, { useMemo, useRef } from 'react';
import { NewTaskRow } from 'shared/components/connected/new-task-row';
import {
  RenderItemProps,
  rootId,
  TreeItem,
  TreeItemTypes,
  useTree,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import { useToday } from 'shared/contexts/today';
import { useTaskDateShortcuts } from 'shared/hooks/use-task-date-shortcuts';
import { useTaskPriorityShortcuts } from 'shared/hooks/use-task-priority-shortcuts';
import { ID } from 'shared/types/id';
import {
  noDeadlineSectionID,
  overdueSectionID,
} from 'shared/types/orderable-section';
import { Task } from 'shared/types/task';
import { parseDateKey } from 'shared/utils/format-date-key';

import { useDeadlineDeadlineTree } from '../../hooks/use-deadline-deadline-tree';
import { GroupedListProps } from '../../types';
import { DeadlineHeader } from '../deadline-header';
import { TreeTaskCard } from '../tree-task-card';
import * as Styled from './deadline-deadline-grouping-tasks-list.style';

const taskTreeItemTypes = [TreeItemTypes.Task];

export const DeadlineDeadlineGroupingTasksList: React.FC<GroupedListProps> = ({
  tasks,
  sortingMode,
  onReorder,
  customOrder = {},
  collapsedSections = [],
  onSectionCollapse,
  weekStartsOn,
  scrollElement,
  denseFormat,
  groupSettings,
  isContextMenuOpen,
}) => {
  const today = useToday();
  const { submit: updateTasks } = useUpdateTasks();

  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  const treeData = useDeadlineDeadlineTree({
    tasks,
    customOrder,
    sortingMode,
    today,
    weekStartsOn,
    showEmptyGroups: groupSettings.showEmptyGroups,
  });

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }

    const updatedDocs: Task[] = [];
    const updatedSections: { id: ID; taskSorting: ID[] }[] = [];

    // Process each task to detect section changes
    updatedItems.forEach((item) => {
      if (item.type === TreeItemTypes.Task) {
        if (!tasksMap[item.id]) {
          return;
        }

        const task = tasksMap[item.id];
        const taskToUpdate = structuredClone(task);

        // Find which section this task is now in
        const newSectionItem = updatedItems.find((section) =>
          section.children?.includes(item.id),
        );

        if (!newSectionItem) {
          return;
        }

        const newSectionId = newSectionItem.id;

        // prevent tasks from being moved to overdue
        if (newSectionId === overdueSectionID) {
          return;
        }

        // If moved to "No Deadline", remove deadlineDeadline
        if (newSectionId === noDeadlineSectionID) {
          if (task.deadlineDeadline) {
            taskToUpdate.deadlineDeadline = null;
            updatedDocs.push(taskToUpdate);
          }
          return;
        }

        // Moves to a Date Section
        const newDate = parseDateKey(newSectionId);
        const newDeadline = endOfDay(newDate);

        if (
          !task.deadlineDeadline ||
          task.deadlineDeadline.getTime() !== newDeadline.getTime()
        ) {
          taskToUpdate.deadlineDeadline = newDeadline;
          updatedDocs.push(taskToUpdate);
        }
        return;
      }
    });

    onReorder(updatedSections, updatedDocs);
  };

  // using refs to prevent the renderItem from rerendering all items
  const tasksMapRef = useRef(tasksMap);
  tasksMapRef.current = tasksMap;

  const tasksRef = useRef(tasks);
  tasksRef.current = tasks;

  const renderItem: React.FC<RenderItemProps> = useMemo(
    () =>
      ({ item, itemInstance, tree, isMultiSelectModifierPressed }) => {
        const type = item.type;

        // Task
        if (type === TreeItemTypes.Task) {
          const currentTasksMap = tasksMapRef.current;
          const currentTasks = tasksRef.current;

          return currentTasksMap[item.sourceId] ? (
            <TreeTaskCard
              task={currentTasksMap[item.sourceId]}
              tree={tree}
              item={itemInstance}
              tasks={currentTasks}
              isMultiSelectModifierPressed={isMultiSelectModifierPressed}
              displayDue={groupSettings.displayDue}
            />
          ) : null;
        }

        // New Task
        if (type === TreeItemTypes.NewTask) {
          const dateStr = item.sourceId.replace('-new-task', '');
          const deadlineDeadline =
            dateStr === noDeadlineSectionID
              ? undefined
              : dateStr === overdueSectionID
                ? endOfDay(addDays(today, -1))
                : parseDateKey(dateStr);

          return (
            <NewTaskRow
              deadlineDeadline={deadlineDeadline}
              startTime={null}
              duration={null}
              goal={groupSettings.goalId}
              lifeArea={groupSettings.lifeAreaId}
              tags={groupSettings.tagId ? [groupSettings.tagId] : undefined}
            />
          );
        }

        // Date
        if (type === TreeItemTypes.Date) {
          return (
            <DeadlineHeader denseFormat={denseFormat} item={itemInstance} />
          );
        }

        return null;
      },
    [
      today,
      denseFormat,
      groupSettings.displayDue,
      groupSettings.goalId,
      groupSettings.lifeAreaId,
      groupSettings.tagId,
    ],
  );

  const [tree] = useTree();

  useTaskPriorityShortcuts(tree, tasks, updateTasks);
  useTaskDateShortcuts(tree, tasks, updateTasks);

  return (
    <Styled.Container>
      <VirtualizedTreeList
        rootItemId={rootId}
        items={treeData}
        renderItem={renderItem}
        scrollElement={scrollElement}
        multiselectTypes={taskTreeItemTypes}
        dragTypes={taskTreeItemTypes}
        onReorder={_onReorder}
        collapsedItems={collapsedSections}
        onCollapse={onSectionCollapse}
        denseFormat={denseFormat}
        isContextMenuOpen={isContextMenuOpen}
      />
    </Styled.Container>
  );
};
